import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Coffee, Layers, Target, Trophy, Award, Crown, Star, MessageCircle, Sunrise, Search, Plus, X, Lock, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

const ICON_MAP = { Coffee, Layers, Target, Trophy, Award, Crown, Star, MessageCircle, Sunrise };
const TIER_STYLES = {
  bronze: { bg: "from-amber-700 to-amber-900", ring: "ring-amber-700/30", label_en: "Bronze", label_ar: "برونزي" },
  silver: { bg: "from-stone-400 to-stone-600", ring: "ring-stone-500/30", label_en: "Silver", label_ar: "فضي" },
  gold: { bg: "from-amber-400 to-amber-600", ring: "ring-amber-500/40", label_en: "Gold", label_ar: "ذهبي" },
};

export default function AdminAchievementsTab() {
  const { isRTL, lang } = useI18n();
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: endpoints.adminUsers,
  });

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users.slice(0, 50);
    return users.filter((u) => (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q)).slice(0, 50);
  }, [users, query]);

  const { data: userAch, isLoading: achLoading, refetch } = useQuery({
    queryKey: ["admin", "user-achievements", selectedUserId],
    queryFn: () => endpoints.adminUserAchievements(selectedUserId),
    enabled: !!selectedUserId,
  });

  const grant = async (key) => {
    if (!selectedUserId) return;
    setBusyKey(key);
    try {
      const res = await endpoints.adminGrantAchievement(selectedUserId, key);
      if (res.already_unlocked) {
        toast.info(isRTL ? "الشارة مفتوحة مسبقًا" : "Badge already unlocked");
      } else {
        toast.success(isRTL ? `✓ تم منح: ${res.achievement.title_ar}` : `✓ Granted: ${res.achievement.title_en}`);
      }
      await refetch();
      qc.invalidateQueries({ queryKey: ["my-achievements"] });
    } catch (e) {
      toast.error(isRTL ? "فشل المنح" : "Grant failed");
    } finally {
      setBusyKey(null);
    }
  };

  const revoke = async (key) => {
    if (!selectedUserId) return;
    setBusyKey(key);
    try {
      const res = await endpoints.adminRevokeAchievement(selectedUserId, key);
      if (res.removed > 0) {
        toast.success(isRTL ? "✓ تم سحب الشارة" : "✓ Badge revoked");
      } else {
        toast.info(isRTL ? "الشارة غير ممنوحة" : "Badge not granted");
      }
      await refetch();
      qc.invalidateQueries({ queryKey: ["my-achievements"] });
    } catch (e) {
      toast.error(isRTL ? "فشل السحب" : "Revoke failed");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="admin-achievements-tab">
      {/* Left: user search */}
      <div className="lg:col-span-4">
        <div className="soft-card rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#C86240]" />
            <h3 className="font-semibold text-[#2A2421]">{isRTL ? "اختر مستخدمًا" : "Pick a user"}</h3>
          </div>
          <div className="relative mb-3">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-[#5C544F]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isRTL ? "ابحث بالإيميل أو الاسم…" : "Search by email or name…"}
              className="ps-9 rounded-full"
              data-testid="admin-ach-user-search"
            />
          </div>
          <div className="max-h-[480px] overflow-auto -mx-1 px-1 space-y-1.5" data-testid="admin-ach-user-list">
            {usersLoading && <div className="text-sm text-[#5C544F]">…</div>}
            {!usersLoading && filteredUsers.length === 0 && (
              <div className="text-sm text-[#5C544F] text-center py-6">
                {isRTL ? "لا نتائج" : "No results"}
              </div>
            )}
            {filteredUsers.map((u) => (
              <button
                key={u.user_id}
                onClick={() => setSelectedUserId(u.user_id)}
                data-testid={`admin-ach-user-${u.user_id}`}
                className={`w-full text-start px-3 py-2 rounded-xl border transition-colors ${
                  selectedUserId === u.user_id
                    ? "border-[#C86240] bg-[#C86240]/10"
                    : "border-transparent hover:border-[#2A2421]/15 hover:bg-[#F9F6F0]"
                }`}
              >
                <div className="font-medium text-sm text-[#2A2421] truncate">{u.name || "—"}</div>
                <div className="text-xs text-[#5C544F] truncate">{u.email}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right: badges grid */}
      <div className="lg:col-span-8">
        {!selectedUserId && (
          <div className="soft-card rounded-2xl p-10 text-center text-[#5C544F]" data-testid="admin-ach-empty-state">
            <Trophy className="w-10 h-10 mx-auto mb-3 text-[#C86240]/40" />
            <div>{isRTL ? "اختر مستخدمًا من اليمين لعرض شاراته." : "Pick a user on the left to manage their badges."}</div>
          </div>
        )}

        {selectedUserId && (
          <div className="soft-card rounded-2xl p-5" data-testid="admin-ach-detail">
            {achLoading && <div className="text-sm text-[#5C544F]">…</div>}
            {userAch && (
              <>
                <div className="flex flex-wrap items-center gap-3 mb-5 pb-4 border-b border-[#2A2421]/10">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold text-[#2A2421]">{userAch.user.name || "—"}</div>
                    <div className="text-xs text-[#5C544F]">{userAch.user.email}</div>
                  </div>
                  <div className="text-sm text-[#C86240] font-semibold" data-testid="admin-ach-progress">
                    {userAch.unlocked_count}/{userAch.total} {isRTL ? "شارة" : "badges"}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {userAch.items.map((a) => {
                    const Icon = ICON_MAP[a.icon] || Trophy;
                    const tier = TIER_STYLES[a.tier] || TIER_STYLES.bronze;
                    const isBusy = busyKey === a.key;
                    return (
                      <div
                        key={a.key}
                        data-testid={`admin-ach-card-${a.key}`}
                        className={`rounded-xl border p-3 flex gap-3 items-start ${
                          a.unlocked ? "border-[#C86240]/40 bg-[#C86240]/5" : "border-[#2A2421]/10 bg-white"
                        }`}
                      >
                        <div
                          className={`relative shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${tier.bg} flex items-center justify-center ring-2 ${tier.ring} ${
                            a.unlocked ? "" : "opacity-40 grayscale"
                          }`}
                        >
                          <Icon className="w-5 h-5 text-white" />
                          {!a.unlocked && (
                            <div className="absolute inset-0 rounded-xl bg-black/30 flex items-center justify-center">
                              <Lock className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            <span className="text-[9px] uppercase tracking-widest text-[#5C544F]">
                              {isRTL ? tier.label_ar : tier.label_en}
                            </span>
                            {a.manual && (
                              <span className="text-[9px] uppercase tracking-widest text-[#C86240] bg-[#C86240]/10 px-1.5 rounded-full">
                                {isRTL ? "ممنوحة يدويًا" : "Manual"}
                              </span>
                            )}
                            {a.secret && (
                              <span className="text-[9px] uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 rounded-full">
                                {isRTL ? "سرّية" : "Secret"}
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-semibold text-[#2A2421] leading-tight">
                            {lang === "ar" ? a.title_ar : a.title_en}
                          </div>
                          <div className="text-[11px] text-[#5C544F] mt-0.5 leading-snug">
                            {lang === "ar" ? a.desc_ar : a.desc_en}
                          </div>
                          <div className="mt-2 flex gap-2">
                            {!a.unlocked ? (
                              <Button
                                size="sm"
                                onClick={() => grant(a.key)}
                                disabled={isBusy}
                                data-testid={`admin-ach-grant-${a.key}`}
                                className="h-7 px-2.5 rounded-full bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0] text-xs"
                              >
                                <Plus className="w-3 h-3 me-1" />
                                {isRTL ? "منح" : "Grant"}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => revoke(a.key)}
                                disabled={isBusy}
                                data-testid={`admin-ach-revoke-${a.key}`}
                                className="h-7 px-2.5 rounded-full border-[#C86240]/40 text-[#C86240] hover:bg-[#C86240]/10 text-xs"
                              >
                                <X className="w-3 h-3 me-1" />
                                {isRTL ? "سحب" : "Revoke"}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
